import { z } from 'zod';
import { isoDateTime, markdown, mediumText, shortText, slug, uuid, } from './primitives.js';
import { sessionComponentBadges } from './courses.js';
import { sessionResourceLibrary } from './resources.js';
import { sessionDeck } from './decks.js';
// ------------------------------------------------------------------- video ---
export const videoStatus = z.enum([
    'awaiting_upload',
    'uploading',
    'uploaded',
    'transcoding',
    'ready',
    'failed',
]);
export const videoRendition = z.object({
    name: z.string(),
    height: z.number().int(),
    bitrateKbps: z.number().int(),
});
export const videoChapter = z.object({
    id: uuid,
    title: z.string(),
    startSec: z.number(),
    orderIndex: z.number().int(),
});
/** Author-facing video state, including pipeline progress. */
export const videoAdminView = z.object({
    id: uuid,
    sessionId: uuid,
    status: videoStatus,
    progressPct: z.number().int(),
    stage: z.string().nullable(),
    error: z.string().nullable(),
    originalFilename: z.string().nullable(),
    originalSize: z.number().nullable(),
    durationSec: z.number().nullable(),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    renditions: z.array(videoRendition),
    posterUrl: z.string().nullable(),
    hasCaptions: z.boolean(),
    transcript: z.string().nullable(),
    transcriptSource: z.enum(['asr', 'manual', 'imported']).nullable(),
    transcriptUpdatedAt: isoDateTime.nullable(),
    chapters: z.array(videoChapter),
    updatedAt: isoDateTime,
});
/**
 * Learner-facing playback payload. `hlsUrl` is a short-TTL signed URL; there is
 * deliberately no progressive-MP4 field anywhere in this contract.
 */
export const videoPlayback = z.object({
    id: uuid,
    status: videoStatus,
    hlsUrl: z.string().nullable(),
    posterUrl: z.string().nullable(),
    captionsUrl: z.string().nullable(),
    durationSec: z.number().nullable(),
    chapters: z.array(videoChapter),
    /** Seconds until hlsUrl must be re-fetched. The player refreshes ahead of it. */
    expiresInSec: z.number().int(),
});
// upload -----------------------------------------------------------------------
export const uploadInitRequest = z.object({
    filename: z.string().min(1).max(512),
    size: z.number().int().min(1),
    mime: z.string().max(200),
    partSizeBytes: z
        .number()
        .int()
        .min(5 * 1024 * 1024)
        .max(512 * 1024 * 1024)
        .default(16 * 1024 * 1024),
});
export const uploadInitResponse = z.object({
    videoId: uuid,
    uploadId: z.string(),
    key: z.string(),
    partSizeBytes: z.number().int(),
    partCount: z.number().int(),
});
export const uploadPartUrlsRequest = z.object({
    uploadId: z.string(),
    partNumbers: z.array(z.number().int().min(1).max(10_000)).min(1).max(100),
});
export const uploadPartUrlsResponse = z.object({
    urls: z.array(z.object({ partNumber: z.number().int(), url: z.string() })),
    expiresInSec: z.number().int(),
});
export const uploadCompleteRequest = z.object({
    uploadId: z.string(),
    parts: z
        .array(z.object({ partNumber: z.number().int(), etag: z.string() }))
        .min(1),
    /** Kick off ASR alongside transcode. Off unless asked: transcripts come from the meeting recording. */
    transcribe: z.boolean().default(false),
});
export const updateTranscriptRequest = z.object({
    transcript: z.string().max(1_000_000),
    /** Regenerate WebVTT captions and re-chunk for retrieval. */
    regenerateCaptions: z.boolean().default(true),
    /** 'imported' when the text is a converted transcript file the author has not edited. */
    source: z.enum(['manual', 'imported']).default('manual'),
});
/**
 * A transcript file (.docx or .vtt from the meeting recording) converted to
 * the timed format, for the author to review in the editor. Nothing is saved.
 */
export const transcriptImportResponse = z.object({
    format: z.enum(['docx', 'vtt']),
    transcript: z.string(),
    lineCount: z.number().int(),
    firstSec: z.number().nullable(),
    lastSec: z.number().nullable(),
    /** Speakers kept by name: the session's instructors and the course's admins. */
    namedSpeakers: z.array(z.string()),
    /** Other speakers, shown as "Participant" (or "Instructor" when they present). */
    anonymisedSpeakers: z.number().int(),
    /** Mismatches with the video worth a look before saving, e.g. a transcript that runs past its end. */
    warnings: z.array(z.string()),
});
export const chaptersRequest = z.object({
    chapters: z
        .array(z.object({
        title: shortText.min(1),
        startSec: z.number().min(0),
    }))
        .max(200),
});
// ------------------------------------------------------------- content block ---
export const contentBlockKind = z.enum(['markdown', 'file', 'link']);
export const contentBlock = z.object({
    id: uuid,
    kind: contentBlockKind,
    title: z.string(),
    bodyMd: z.string().nullable(),
    fileName: z.string().nullable(),
    fileSize: z.number().nullable(),
    /**
     * The storage key behind a file block. It is not a credential — every read
     * goes through `/api/files/*`, which re-applies the page's own access check —
     * and it is carried here so an author can edit a file block's heading without
     * the PATCH detaching the file it cannot name.
     */
    fileKey: z.string().nullable(),
    /** Signed, short-TTL. Null for non-file blocks. */
    downloadUrl: z.string().nullable(),
    url: z.string().nullable(),
    orderIndex: z.number().int(),
});
export const upsertContentBlockRequest = z.object({
    kind: contentBlockKind,
    title: shortText.default(''),
    bodyMd: markdown.nullable().default(null),
    url: z.string().url().max(2_000).nullable().default(null),
    /**
     * Storage key returned by the generic file-upload endpoint. On a PATCH an
     * absent `fileKey` means "leave the attached file where it is"; an explicit
     * `null` detaches it.
     */
    fileKey: z.string().max(1_024).nullable().optional(),
    fileName: z.string().max(512).nullable().default(null),
    fileSize: z.number().int().nullable().default(null),
});
// --------------------------------------------------------------- interactive ---
export const interactiveType = z.enum([
    'embedded_app',
    'external_embed',
    'hosted_app',
    'notebook',
    'flashcards',
    'code_sandbox',
]);
/**
 * How a notebook is shown. `runnable` opens it in JupyterLite, where Python
 * runs in the learner's own browser (Pyodide) — never on the server.
 * `read_only` renders the saved cells and outputs without a kernel.
 */
export const notebookMode = z.enum(['runnable', 'read_only']);
/** What a notebook feature keeps in `config`, beside the platform's own keys. */
export const notebookConfig = z.object({
    mode: notebookMode.default('runnable'),
});
/** Python app frameworks a hosted_app can run. Plain HTML is an embedded_app. */
export const hostedAppRuntime = z.enum(['streamlit', 'gradio', 'dash']);
export const hostedAppPythonVersion = z.enum(['3.11', '3.12', '3.13']);
export const hostedAppStatus = z.enum([
    'queued',
    'checking',
    'building',
    'starting',
    'ready',
    'failed',
]);
/** How a hosted app is built and run. Changing any of it means a rebuild. */
export const hostedAppSettings = z.object({
    runtime: hostedAppRuntime,
    /** Path of the entry script inside the archive, e.g. `app.py`. */
    entryFile: z
        .string()
        .trim()
        .min(1)
        .max(256)
        .regex(/^[A-Za-z0-9_\-./]+\.py$/, 'The entry file must be a .py path inside the archive.'),
    pythonVersion: hostedAppPythonVersion.default('3.12'),
    /** Outbound internet access, e.g. for calling an LLM provider. */
    network: z.boolean().default(false),
});
/**
 * Environment variables a hosted app runs with (API keys and the like).
 *
 * They are write-only: the API stores them encrypted and only ever returns the
 * names. Anything the app's own code can read it can also print, so they are
 * for keys that are low-privilege or budget-capped.
 */
export const HOSTED_APP_ENV_MAX_VARS = 50;
export const HOSTED_APP_ENV_MAX_VALUE = 4096;
/** Names the platform sets itself, or that would stop the app serving or the runtime starting. */
const RESERVED_ENV_NAMES = new Set([
    'PORT',
    'HOME',
    'PATH',
    'HOSTNAME',
    'USER',
    'LD_PRELOAD',
    'LD_LIBRARY_PATH',
    'GRADIO_SERVER_NAME',
    'GRADIO_SERVER_PORT',
]);
export const hostedAppEnvKey = z
    .string()
    .trim()
    .min(1, 'Give the variable a name.')
    .max(64, 'A variable name is at most 64 characters.')
    .regex(/^[A-Za-z_][A-Za-z0-9_]*$/, 'A variable name uses letters, digits and underscores, and does not start with a digit.')
    .refine((k) => !RESERVED_ENV_NAMES.has(k.toUpperCase()) && !/^(LP_|DOCKER_)/i.test(k), { error: (issue) => `${String(issue.input)} is set by the platform and cannot be overridden.` });
export const hostedAppEnvEntry = z.object({
    key: hostedAppEnvKey,
    /**
     * The new value. Omitted (or null) keeps the value already stored for this
     * name: the UI never has the stored values to send back.
     */
    value: z
        .string()
        .max(HOSTED_APP_ENV_MAX_VALUE, `A value is at most ${HOSTED_APP_ENV_MAX_VALUE} characters.`)
        .refine((v) => !/[\r\n\0]/.test(v), 'A value must be a single line.')
        .nullish(),
});
/** The complete set of variables the app should have: names left out are removed. */
export const hostedAppEnvSpec = z
    .array(hostedAppEnvEntry)
    .max(HOSTED_APP_ENV_MAX_VARS, `At most ${HOSTED_APP_ENV_MAX_VARS} variables.`)
    .refine((entries) => new Set(entries.map((e) => e.key)).size === entries.length, {
    message: 'Each variable name can appear only once.',
});
/**
 * Internet access and environment variables for code that runs in the
 * learner's browser: a runnable notebook or an embedded app.
 *
 * Internet access is a list of exact sites added to the frame's CSP, never
 * "anywhere". The variables reuse the hosted-app rules, but they are handed to
 * the browser of whoever opens the notebook or app, where its code (and the
 * learner, through dev tools) can read them.
 */
export const BROWSER_NETWORK_MAX_HOSTS = 20;
/** A hostname, optionally with a leading `*.` for its subdomains. No scheme, port or path. */
export const browserNetworkHost = z
    .string()
    .trim()
    .toLowerCase()
    .max(253)
    .regex(/^(\*\.)?(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z][a-z0-9-]{0,61}[a-z0-9]$/, 'Give a site as a hostname, e.g. api.openai.com or *.openai.azure.com.');
/** Sites an author can add with one click: the usual LLM provider endpoints. */
export const BROWSER_NETWORK_PRESETS = [
    { label: 'OpenAI', hosts: ['api.openai.com'] },
    { label: 'Anthropic', hosts: ['api.anthropic.com'] },
    { label: 'Google Gemini', hosts: ['generativelanguage.googleapis.com'] },
    { label: 'Azure OpenAI', hosts: ['*.openai.azure.com', '*.services.ai.azure.com'] },
    { label: 'Groq', hosts: ['api.groq.com'] },
    { label: 'OpenRouter', hosts: ['openrouter.ai'] },
];
/** What the console shows about a notebook's or embedded app's access. */
export const browserAccess = z.object({
    network: z.boolean(),
    hosts: z.array(z.string()),
    /** Names of the variables set (never their values); empty for learners. */
    envKeys: z.array(z.string()),
    /** Whether any variables are set: learners' frames fetch them only then. */
    hasEnv: z.boolean(),
});
/** PUT /api/admin/interactive/:featureId/access */
export const updateBrowserAccessRequest = z.object({
    network: z.boolean(),
    hosts: z
        .array(browserNetworkHost)
        .max(BROWSER_NETWORK_MAX_HOSTS, `At most ${BROWSER_NETWORK_MAX_HOSTS} sites.`)
        .default([]),
    /** Omitted: keep the variables as they are. */
    env: hostedAppEnvSpec.optional(),
});
/** GET /api/interactive/:featureId/env — an embedded app's variables, for its frame. */
export const browserEnvResponse = z.object({
    env: z.record(z.string(), z.string()),
});
/** One build of an uploaded app. Identical uploads share a build. */
export const hostedAppSummary = hostedAppSettings.extend({
    id: uuid,
    status: hostedAppStatus,
    /** Names of the environment variables set (never their values); empty for learners. */
    envKeys: z.array(z.string()),
    /** Author-facing reason for a failed build; null for learners. */
    error: z.string().nullable(),
    warnings: z.array(z.string()),
    hasLog: z.boolean(),
    createdAt: isoDateTime,
    readyAt: isoDateTime.nullable(),
});
export const interactiveFeature = z.object({
    id: uuid,
    type: interactiveType,
    title: z.string(),
    description: z.string(),
    config: z.record(z.string(), z.unknown()),
    /** Absolute URL on the sandbox origin (embedded_app) or the allow-listed target. */
    embedUrl: z.string().nullable(),
    /**
     * external_embed only: the host is on EMBED_TRUSTED_HOSTS, so the frame runs
     * with its own origin (storage, Cache API) rather than an opaque one.
     */
    embedTrusted: z.boolean().default(false),
    heightPx: z.number().int(),
    enabled: z.boolean(),
    orderIndex: z.number().int(),
    bundleReady: z.boolean(),
    /** hosted_app only: the build learners are served. */
    app: hostedAppSummary.nullable().default(null),
    /** hosted_app only, authors only: a newer build still in progress or failed. */
    pendingApp: hostedAppSummary.nullable().default(null),
    /**
     * notebook only. `path` is the notebook inside the upload, once extracted.
     * A runnable notebook's frame address is `embedUrl`; it is null when the
     * notebook is read-only or this server cannot run notebooks.
     */
    notebook: z
        .object({ mode: notebookMode, path: z.string().nullable() })
        .nullable()
        .default(null),
    /** notebook and embedded_app only: internet access and environment variables. */
    access: browserAccess.nullable().default(null),
    progress: z
        .object({ viewed: z.boolean(), completed: z.boolean() })
        .nullable()
        .default(null),
});
export const upsertInteractiveRequest = z.object({
    type: interactiveType,
    title: shortText.min(1),
    description: mediumText.default(''),
    config: z.record(z.string(), z.unknown()).default({}),
    url: z.string().url().max(2_000).nullable().default(null),
    entryPath: z.string().max(256).default('index.html'),
    heightPx: z.number().int().min(160).max(2_000).default(520),
    enabled: z.boolean().default(true),
});
export const interactiveEventRequest = z.object({
    /** The entire vocabulary of the postMessage bridge. */
    event: z.enum(['viewed', 'completed']),
});
/** POST /api/interactive/:featureId/launch */
export const appLaunchResponse = z.object({
    /** A per-viewer origin on the apps host; the capability is in the hostname. */
    url: z.string(),
    expiresAt: isoDateTime,
});
/**
 * GET /api/interactive/:featureId/notebook/grant — the frame address for a
 * runnable notebook with environment variables. It carries a short-lived pass
 * that the notebook origin swaps for a cookie, so the variables reach this
 * viewer's kernel and nobody else's.
 */
export const notebookGrantResponse = appLaunchResponse;
/**
 * GET /api/interactive/:featureId/notebook — the saved notebook document, for
 * the read-only view and for "download". The document is author content and
 * is rendered client-side without executing anything in it.
 */
export const notebookDocumentResponse = z.object({
    fileName: z.string(),
    notebook: z.record(z.string(), z.unknown()),
});
/** POST /api/admin/interactive/:featureId/app/rebuild */
export const rebuildHostedAppRequest = hostedAppSettings.extend({
    /** Omitted: keep the variables as they are. */
    env: hostedAppEnvSpec.optional(),
});
export const embedAllowlistResponse = z.object({
    hosts: z.array(z.string()),
    /** Exact hosts whose embeds run with their own origin. */
    trustedHosts: z.array(z.string()).default([]),
    /** Whether this server can build and run hosted_app uploads. */
    hostedAppsEnabled: z.boolean().default(false),
    /** Whether this server serves JupyterLite, so a notebook can be runnable. */
    notebooksRunnable: z.boolean().default(false),
});
// -------------------------------------------------------------------- quiz ---
export const questionKind = z.enum([
    'single_select',
    'multi_select',
    'true_false',
    'short_answer',
]);
/** What a learner receives before submitting: no is_correct, no answer keys. */
export const quizQuestionForLearner = z.object({
    id: uuid,
    kind: questionKind,
    promptMd: z.string(),
    points: z.number().int(),
    orderIndex: z.number().int(),
    options: z.array(z.object({ id: uuid, label: z.string(), orderIndex: z.number().int() })),
});
export const quizForLearner = z.object({
    id: uuid,
    title: z.string(),
    descriptionMd: z.string(),
    attemptLimit: z.number().int().nullable(),
    passThresholdPct: z.number().int(),
    questions: z.array(quizQuestionForLearner),
    attemptsUsed: z.number().int(),
    attemptsRemaining: z.number().int().nullable(),
    lastAttempt: z
        .object({
        id: uuid,
        attemptNumber: z.number().int(),
        scorePct: z.number(),
        passed: z.boolean(),
        submittedAt: isoDateTime,
    })
        .nullable(),
});
export const quizAnswer = z.object({
    questionId: uuid,
    /** Option ids for select kinds; free text for short_answer. */
    optionIds: z.array(uuid).default([]),
    text: z.string().max(2_000).nullable().default(null),
});
export const submitQuizRequest = z.object({
    answers: z.array(quizAnswer),
});
/** Per-question feedback, revealed only in the submission response. */
export const quizQuestionResult = z.object({
    questionId: uuid,
    correct: z.boolean(),
    pointsEarned: z.number().int(),
    pointsPossible: z.number().int(),
    correctOptionIds: z.array(uuid),
    explanationMd: z.string(),
});
export const quizAttemptResult = z.object({
    attemptId: uuid,
    attemptNumber: z.number().int(),
    scorePct: z.number(),
    pointsEarned: z.number().int(),
    pointsTotal: z.number().int(),
    passed: z.boolean(),
    passThresholdPct: z.number().int(),
    submittedAt: isoDateTime,
    results: z.array(quizQuestionResult),
    attemptsRemaining: z.number().int().nullable(),
});
// author-side quiz ------------------------------------------------------------
export const quizQuestionForAuthor = z.object({
    id: uuid,
    kind: questionKind,
    promptMd: z.string(),
    explanationMd: z.string(),
    points: z.number().int(),
    acceptedAnswers: z.array(z.string()).nullable(),
    orderIndex: z.number().int(),
    options: z.array(z.object({
        id: uuid,
        label: z.string(),
        isCorrect: z.boolean(),
        orderIndex: z.number().int(),
    })),
});
export const quizForAuthor = z.object({
    id: uuid,
    sessionId: uuid,
    title: z.string(),
    descriptionMd: z.string(),
    attemptLimit: z.number().int().nullable(),
    passThresholdPct: z.number().int(),
    shuffleQuestions: z.boolean(),
    questions: z.array(quizQuestionForAuthor),
});
export const upsertQuizRequest = z.object({
    title: shortText.min(1).default('Knowledge check'),
    descriptionMd: markdown.default(''),
    attemptLimit: z.number().int().min(1).max(50).nullable().default(null),
    passThresholdPct: z.number().int().min(0).max(100).default(70),
    shuffleQuestions: z.boolean().default(false),
});
export const upsertQuestionRequest = z.object({
    kind: questionKind,
    promptMd: markdown.min(1),
    explanationMd: markdown.default(''),
    points: z.number().int().min(1).max(100).default(1),
    acceptedAnswers: z.array(z.string().max(500)).nullable().default(null),
    options: z
        .array(z.object({
        id: uuid.optional(),
        label: shortText.min(1),
        isCorrect: z.boolean().default(false),
    }))
        .default([]),
});
export const importQuestionsRequest = z.object({
    /** CSV (kind,prompt,explanation,points,option1,correct1,...) or a JSON array. */
    format: z.enum(['csv', 'json']),
    payload: z.string().max(1_000_000),
    replaceExisting: z.boolean().default(false),
});
export const importQuestionsResult = z.object({
    imported: z.number().int(),
    skipped: z.number().int(),
    errors: z.array(z.object({ row: z.number().int(), message: z.string() })),
});
// -------------------------------------------------------------- assignment ---
export const assignmentResource = z.object({
    id: uuid,
    kind: z.enum(['file', 'link']),
    title: z.string(),
    description: z.string(),
    fileName: z.string().nullable(),
    fileSize: z.number().nullable(),
    downloadUrl: z.string().nullable(),
    url: z.string().nullable(),
    orderIndex: z.number().int(),
});
export const assignmentForLearner = z.object({
    id: uuid,
    title: z.string(),
    promptMd: z.string(),
    suggestedApproachMd: z.string().nullable(),
    estimatedEffort: z.string().nullable(),
    hasReferenceSolution: z.boolean(),
    /** Non-null only once the learner has revealed it. */
    referenceSolutionMd: z.string().nullable(),
    solutionRevealed: z.boolean(),
    resources: z.array(assignmentResource),
    done: z.boolean(),
});
export const upsertAssignmentRequest = z.object({
    title: shortText.min(1).default('Take-home practice'),
    promptMd: markdown.default(''),
    suggestedApproachMd: markdown.nullable().default(null),
    referenceSolutionMd: markdown.nullable().default(null),
    estimatedEffort: shortText.nullable().default(null),
});
export const upsertResourceRequest = z.object({
    kind: z.enum(['file', 'link']),
    title: shortText.min(1),
    description: mediumText.default(''),
    url: z.string().url().max(2_000).nullable().default(null),
    fileKey: z.string().max(1_024).nullable().default(null),
    fileName: z.string().max(512).nullable().default(null),
    fileSize: z.number().int().nullable().default(null),
    isStarter: z.boolean().default(false),
});
// ---------------------------------------------------- full session payloads ---
/** Everything the learner session page renders, in one round trip. */
export const sessionDetail = z.object({
    id: uuid,
    courseId: uuid,
    courseSlug: slug,
    courseTitle: z.string(),
    slug,
    title: z.string(),
    summary: z.string(),
    orderIndex: z.number().int(),
    estimatedDurationMin: z.number().int().nullable(),
    status: z.enum(['draft', 'published']),
    /** True when an author is viewing their own unpublished draft. */
    isPreview: z.boolean(),
    components: sessionComponentBadges,
    video: videoPlayback.nullable(),
    /** The session's slide decks, in order. Empty when it has none. */
    decks: z.array(sessionDeck),
    notes: z.array(contentBlock),
    interactive: z.array(interactiveFeature),
    quiz: quizForLearner.nullable(),
    assignment: assignmentForLearner.nullable(),
    /** Reference documents: files and links in folders. Empty when there are none. */
    resources: sessionResourceLibrary,
    progress: z.object({
        watchPct: z.number(),
        lastPositionSec: z.number(),
        completed: z.boolean(),
    }),
    neighbours: z.object({
        prev: z.object({ slug, title: z.string() }).nullable(),
        next: z.object({ slug, title: z.string() }).nullable(),
    }),
    /**
     * Whether the chat panel is available on this page. It is not "a model is
     * attached": the panel retrieves and deep-links into this session's material
     * in stub mode too, and whether the assistant can answer in its own words is
     * `mode` from `GET /api/chat/config`.
     */
    chatEnabled: z.boolean(),
});
//# sourceMappingURL=content.js.map